import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
} from "../../lib";
import { clearLogoutPending } from "../../lib/auth/logout_pending";
import { withLoginClaim } from "../../lib/auth/pending_logout_retry";
import { startDeadlineRequest, startSignedInSession } from "../../lib/auth/session_deadlines";
import { toProofSafeSchemaError } from "../../model/auth_proof";

import { doAUTH_SET, doSETTINGS_SET, doREQUEST_ERROR, doREQUEST_COMPLETE } from "../state";

export const REQUEST_IDENTITY_CREATE = "REQUEST_IDENTITY_CREATE";
export const doREQUEST_IDENTITY_CREATE = createDoRequestAction(REQUEST_IDENTITY_CREATE);

// TODO: auto load doRequest* from saga
export const requestIdentityCreateActionCreators = {
  doREQUEST_IDENTITY_CREATE,
};

export function* requestIdentityCreate({ payload, setRequestResult }: any): SagaIterator {
  debug("saga*requestIdentityCreate");

  // assert payload makes sence
  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        termAgreement: v.boolean().valid(true).exist(),
        email: v.string().exist(),
        password: v.string().min(8).exist(),
      }),
    });
  } catch (error) {
    // the error reduced to `isJoi` and a message: the ValidationError itself quotes the rejected
    // payload, password included (see `toProofSafeSchemaError`)
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_IDENTITY_CREATE,
          requestId: payload?.requestId,
          error: toProofSafeSchemaError(error),
        },
        setRequestResult,
      ),
    );
  }

  // make request api
  const { email, password } = payload;

  // holds the login claim and waits out a logout retry in flight, so neither a retry already out
  // nor one started while this request is on the wire can clear its cookie; see `withLoginClaim`
  return yield* withLoginClaim(function* () {
    const deadlineRequest = startDeadlineRequest();
    // yields a generator that has a promise in its result
    const result = yield request({
      method: "post",
      url: `${API_URL}/v1/web/identity-create`,
      data: {
        email,
        password,
      },
    });

    // handle error
    if (!result.success || result.error) {
      // never the raw error, which carries the request body (password included) on `config.data`,
      // and no `fromPayload`; see "Credential-Carrying Sagas" in src/store/saga/AGENTS.md
      debug("saga*requestIdentityCreate failed", {
        statusCode: result.statusCode,
        type: result.type,
      });
      return yield put(
        doREQUEST_ERROR(
          {
            fromType: REQUEST_IDENTITY_CREATE,
            requestId: payload.requestId,
            url: "/v1/web/identity-create",
            statusCode: result.statusCode,
            type: result.type,
            payload: result.payload,
          },
          setRequestResult,
        ),
      );
    }

    const { settings, accessToken, accessTokenExpiresInSeconds, refreshTokenExpiresInSeconds } =
      result.payload;

    // update state on success
    yield put(
      doAUTH_SET({
        accessToken,
        accessTokenExpiresInSeconds,
        refreshTokenExpiresInSeconds,
        ...startSignedInSession(result.payload, deadlineRequest),
      }),
    );

    // a new session retires the old one's marker, as a login does; left in place, the next retry
    // would end this session on the server
    yield call(clearLogoutPending);

    yield put(doSETTINGS_SET(settings));

    yield put(
      doREQUEST_COMPLETE(
        {
          fromType: REQUEST_IDENTITY_CREATE,
          requestId: payload.requestId,
          ...result,
        },
        setRequestResult,
      ),
    );
  });
}

export function* identityCreateSaga() {
  yield takeEvery(REQUEST_IDENTITY_CREATE, requestIdentityCreate);
}
